import { assign, fromPromise, setup } from "xstate";

/**
 * Bounded session-recovery state machine for {@link ActorProvider}.
 *
 * When a protected route mounts with no session, this machine drives a bounded
 * number of recovery attempts before giving up and letting the provider
 * redirect to /login. It exists to remove the previous ref-during-render
 * pattern: the attempt counter and in-flight status now live in machine state
 * (read via the actor snapshot), so render output never reads a mutable ref.
 *
 * Two modes share one chart because the lifecycle is identical — only the
 * *recovery action* and the *retry trigger* differ:
 *
 * - `cookie` (non-iframe post-signup/login race): Better Auth's session store
 *   can briefly lag the just-set cookie. Each attempt simply re-fetches the
 *   session, and attempts fire automatically on a timer (`retryDelayMs`).
 * - `iframe` (EHR host): the bearer token in localStorage can go stale. Each
 *   attempt asks the parent for a fresh token before re-fetching. The *first*
 *   attempt fires automatically; subsequent attempts fire only when the tab
 *   returns to the foreground (the host can only mint a token for an active
 *   tab), so the component sends `RETRY` on focus/visibilitychange.
 *
 * The machine never reads the session itself — that lives in React's
 * `useSession`. The component translates session presence into `SESSION_FOUND`
 * / `SESSION_ABSENT` events and reads machine state to decide whether to hold
 * the render, redirect, or show children.
 */
export type SessionRecoveryMode = "cookie" | "iframe";

export type SessionRecoveryInput = {
  mode: SessionRecoveryMode;
  maxAttempts: number;
  /**
   * Delay before each auto-attempt. In `cookie` mode this paces every retry;
   * in `iframe` mode it only paces the first (auto) attempt.
   */
  retryDelayMs: number;
  /** Performs one recovery attempt (re-fetch session; iframe also mints a token). */
  recover: () => Promise<void>;
};

export type SessionRecoveryContext = SessionRecoveryInput & {
  attempts: number;
};

export type SessionRecoveryEvent =
  | { type: "SESSION_ABSENT" }
  | { type: "SESSION_FOUND" }
  | { type: "RETRY" };

export const sessionRecoveryMachine = setup({
  types: {
    context: {} as SessionRecoveryContext,
    events: {} as SessionRecoveryEvent,
    input: {} as SessionRecoveryInput,
  },
  actors: {
    recover: fromPromise(
      async ({ input }: { input: { recover: () => Promise<void> } }) => {
        await input.recover();
      },
    ),
  },
  actions: {
    resetAttempts: assign({ attempts: 0 }),
    incrementAttempts: assign({
      attempts: ({ context }) => context.attempts + 1,
    }),
  },
  guards: {
    // cookie mode auto-retries every attempt; iframe mode only auto-fires the
    // first attempt and waits for a foreground RETRY for the rest.
    shouldAutoAttempt: ({ context }) =>
      context.mode === "cookie" || context.attempts === 0,
    exhausted: ({ context }) => context.attempts >= context.maxAttempts,
  },
  delays: {
    retryDelay: ({ context }) => context.retryDelayMs,
  },
}).createMachine({
  id: "sessionRecovery",
  context: ({ input }) => ({ ...input, attempts: 0 }),
  initial: "idle",
  // A session appearing at any point settles recovery immediately, cancelling
  // any pending attempt or in-flight recovery.
  on: {
    SESSION_FOUND: { target: ".settled" },
  },
  states: {
    // No recovery in progress. `idle` is the start state (session unknown);
    // `settled` is re-entered after a successful recovery. Both re-arm a fresh
    // recovery cycle (attempts reset) if the session later goes absent — e.g.
    // an iframe token expiring mid-session.
    idle: {
      on: {
        SESSION_ABSENT: { target: "awaitingAttempt", actions: "resetAttempts" },
      },
    },
    settled: {
      on: {
        SESSION_ABSENT: { target: "awaitingAttempt", actions: "resetAttempts" },
      },
    },
    awaitingAttempt: {
      after: {
        retryDelay: { target: "recovering", guard: "shouldAutoAttempt" },
      },
      on: { RETRY: { target: "recovering" } },
    },
    recovering: {
      entry: "incrementAttempts",
      invoke: {
        src: "recover",
        input: ({ context }) => ({ recover: context.recover }),
        // After an attempt we wait for the component to report the resulting
        // session via SESSION_FOUND (which preempts via the top-level handler).
        // If it doesn't arrive, loop back to awaiting another attempt, or fail
        // once the budget is spent. onError is treated the same as a failed
        // probe — keep trying; the provider surfaces the real error on redirect.
        onDone: [
          { guard: "exhausted", target: "failed" },
          { target: "awaitingAttempt" },
        ],
        onError: [
          { guard: "exhausted", target: "failed" },
          { target: "awaitingAttempt" },
        ],
      },
    },
    failed: {},
  },
});
